import { workspacesCacheOwner } from './workspaces-cache';

/**
 * Each dashboard widget's last view-model, kept for the dashboard iframe. The runtime paints it the instant a widget
 * binds and replaces it when the live read lands (VMR runtime.js, "SWR data cache"). The runtime keeps it in its own
 * localStorage, but this app's iframe is sandboxed without allow-same-origin and cannot open that, so the frame asks
 * the host to keep the entries (`vmstore=1` on the render URL, bridge-shim.js `__symVmStore`).
 *
 * The frame is untrusted widget code, so it only ever reaches entries under its own document path, every value is a
 * bounded string the host never reads, and the whole store is capped. Same owner rule as the Spaces list: an entry
 * answers only for the account of this page load, and sign-out clears it.
 */

const STORAGE_KEY = 'symphony.widget-vm.v1';
const MAX_AGE_MS = 7 * 24 * 60 * 60_000;
const MAX_ENTRY_CHARS = 100_000;
const MAX_TOTAL_CHARS = 1_000_000;
const FLUSH_MS = 300;

interface Entry {
  v: string;
  at: number;
}

interface Snapshot {
  schema: 1;
  owner: string;
  entries: Record<string, Entry>;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The runtime's key for a widget in this document: `symvm:<document path>|<widget>[|<view>]`. */
export function widgetVmKeyPrefix(documentPath: string): string {
  return `symvm:${documentPath}|`;
}

function readEntries(owner: string): Record<string, Entry> {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw || raw.length > MAX_TOTAL_CHARS * 2) return {};
    const value = JSON.parse(raw) as Partial<Snapshot> | null;
    if (!value || value.schema !== 1 || value.owner !== owner || !value.entries || typeof value.entries !== 'object') return {};
    return value.entries;
  } catch {
    return {};
  }
}

function fresh(entry: Entry | undefined, now: number): entry is Entry {
  if (!entry || typeof entry.v !== 'string' || typeof entry.at !== 'number') return false;
  const age = now - entry.at;
  return age >= 0 && age <= MAX_AGE_MS;
}

/** This account's entries for one document, for the frame's snapshot request. */
export function readWidgetVmSnapshot(documentPath: string): Record<string, string> {
  const owner = workspacesCacheOwner();
  if (!owner) return {};
  const prefix = widgetVmKeyPrefix(documentPath);
  const now = Date.now();
  const out: Record<string, string> = {};
  for (const [key, entry] of Object.entries(readEntries(owner))) {
    if (key.startsWith(prefix) && fresh(entry, now)) out[key] = entry.v;
  }
  for (const [key, value] of pending) {
    if (!key.startsWith(prefix)) continue;
    if (value === null) delete out[key];
    else out[key] = value;
  }
  return out;
}

// A dashboard load writes one entry per widget within a moment, so writes are batched into one read-modify-write.
const pending = new Map<string, string | null>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let flushOnHide = false;

/**
 * Saves (or, with `null`, forgets) one widget's view-model for the frame showing `documentPath`. A key outside that
 * document, or a value over the per-entry cap, is ignored.
 */
export function writeWidgetVm(documentPath: string, key: string, value: string | null): void {
  if (!workspacesCacheOwner()) return;
  if (typeof key !== 'string' || !key.startsWith(widgetVmKeyPrefix(documentPath)) || key.length > 500) return;
  if (value !== null && (typeof value !== 'string' || value.length > MAX_ENTRY_CHARS)) return;
  pending.set(key, value);
  if (!flushTimer) flushTimer = setTimeout(flushWidgetVm, FLUSH_MS);
  // A reload inside the batching window would otherwise drop the last writes.
  if (!flushOnHide && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    flushOnHide = true;
    window.addEventListener('pagehide', () => flushWidgetVm());
  }
}

/** Writes the batched changes now (also used by tests). */
export function flushWidgetVm(): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = undefined;
  const owner = workspacesCacheOwner();
  if (!owner || pending.size === 0) {
    pending.clear();
    return;
  }
  const now = Date.now();
  const entries: Record<string, Entry> = {};
  for (const [key, entry] of Object.entries(readEntries(owner))) {
    if (fresh(entry, now)) entries[key] = entry;
  }
  for (const [key, value] of pending) {
    delete entries[key];
    if (value !== null) entries[key] = { v: value, at: now };
  }
  pending.clear();
  // Oldest first, so the newest survive the cap.
  const ordered = Object.entries(entries).sort((a, b) => a[1].at - b[1].at);
  let total = ordered.reduce((n, [key, entry]) => n + key.length + entry.v.length, 0);
  while (ordered.length && total > MAX_TOTAL_CHARS) {
    const [key, entry] = ordered.shift()!;
    total -= key.length + entry.v.length;
  }
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify({ schema: 1, owner, entries: Object.fromEntries(ordered) } satisfies Snapshot));
  } catch {
    /* quota or private mode: the next load paints skeletons, as before */
  }
}

/** Forgets every kept view-model (sign-out). */
export function clearWidgetVmStore(): void {
  pending.clear();
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = undefined;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to forget where nothing could be written */
  }
}
